import { formatCedis, type GarageKind } from '@accra/shared';
import type { GarageState } from '../../meta/garage';
import { el, setText } from '../components/el';
import { Screen } from '../components/screen';
import { hornRows, paintRows, stickerRows, upgradeRows, type RowHandlers } from './garageRows';

type Tab = 'paint' | 'sticker' | 'horn' | 'upgrades';
const TABS: readonly [Tab, string][] = [['paint', 'Paint'], ['sticker', 'Sticker'], ['horn', 'Horn'], ['upgrades', 'Upgrades']];

export interface GarageActions extends RowHandlers {
  close(): void;
}

/** Paint, stickers and horns for your trotro, bought with wallet money. Changes show on the trotro behind the panel. */
export class Garage extends Screen {
  private tab: Tab = 'paint';
  private readonly wallet = el('span', 'garage-wallet');
  private readonly list = el('div', 'garage-list');
  private readonly message = el('p', 'garage-message');
  private readonly tabs = new Map<Tab, HTMLButtonElement>();
  private state: GarageState | null = null;
  private walletPesewas = 0;

  constructor(parent: HTMLElement, private readonly actions: GarageActions) {
    super(parent, 'garage side-panel');
    const head = el('div', 'garage-head');
    head.append(el('h2', '', 'Garage'), this.wallet);
    const tabs = el('div', 'tabs');
    for (const [id, label] of TABS) {
      const button = el('button', 'tab', label);
      button.addEventListener('click', () => {
        this.tab = id;
        this.render();
      });
      this.tabs.set(id, button);
      tabs.append(button);
    }
    this.panel.append(head, tabs, this.list, this.message, this.button('Done', actions.close, 'primary wide'));
  }

  open(state: GarageState, walletPesewas: number): void {
    this.message.textContent = '';
    this.update(state, walletPesewas);
    this.show();
  }

  update(state: GarageState, walletPesewas: number): void {
    this.state = state;
    this.walletPesewas = walletPesewas;
    this.render();
  }

  say(text: string): void {
    this.message.textContent = text;
  }

  private render(): void {
    const { state } = this;
    if (!state) return;
    setText(this.wallet, formatCedis(this.walletPesewas));
    this.wallet.classList.toggle('debt', this.walletPesewas < 0);
    this.tabs.forEach((button, id) => button.classList.toggle('active', id === this.tab));
    const h: RowHandlers = {
      equip: (kind: GarageKind, id, text) => this.actions.equip(kind, id, text),
      buy: (kind: GarageKind, id, text) => this.actions.buy(kind, id, text),
      previewHorn: (id) => this.actions.previewHorn(id),
    };
    const rows =
      this.tab === 'paint' ? paintRows(state, this.walletPesewas, h)
      : this.tab === 'sticker' ? stickerRows(state, this.walletPesewas, h)
      : this.tab === 'horn' ? hornRows(state, this.walletPesewas, h)
      : upgradeRows(state, this.walletPesewas, h);
    this.list.replaceChildren(...rows);
    if (this.walletPesewas < 0) this.say('You owe money: pay it off with fares before buying anything.');
  }
}
